/**
 * Collects free-text title/role lines (CEO, Co-Founder, etc.) for toolbar
 * Title markers before they are burned into the PDF.
 */
import { useEffect, useState } from 'react';
import { APP } from '../app/tokens';
import { PDF_VIEWER } from './pdfViewerTokens';

interface PrintedTitleModalProps {
  open: boolean;
  initialValue: string;
  /** Shown in subcopy so users know which marker they are filling. */
  fieldLabel?: string;
  onSave: (value: string) => void;
  onClose: () => void;
}

export default function PrintedTitleModal({
  open,
  initialValue,
  fieldLabel,
  onSave,
  onClose,
}: PrintedTitleModalProps) {
  const [draft, setDraft] = useState(initialValue);

  useEffect(() => {
    if (open) setDraft(initialValue);
  }, [open, initialValue]);

  if (!open) return null;

  const trimmed = draft.trim();
  const canSave = trimmed.length > 0;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        backgroundColor: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 440, maxWidth: 'calc(100vw - 48px)',
          backgroundColor: APP.bgContent, color: APP.textPrimary,
          borderRadius: 8, padding: 20,
          boxShadow: '0 12px 48px rgba(0,0,0,0.25)',
          fontFamily: 'inherit',
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>
          Title or role
        </div>
        <div style={{ fontSize: 13, color: APP.textSecondary, marginBottom: 12, lineHeight: 1.45 }}>
          Enter how this line should read on the document (for example CEO, Co-Founder, or Attorney).
          {fieldLabel ? (
            <>
              {' '}
              <span style={{ color: APP.textMuted }}>({fieldLabel})</span>
            </>
          ) : null}
        </div>
        <input
          autoFocus
          type="text"
          value={draft}
          placeholder="e.g. Chief Executive Officer"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canSave) {
              onSave(trimmed);
            } else if (e.key === 'Escape') {
              onClose();
            }
          }}
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '10px 12px', borderRadius: 6,
            border: `1px solid ${APP.border}`,
            fontSize: 14,
            marginBottom: 16,
            backgroundColor: APP.bgContent,
            color: APP.textPrimary,
          }}
        />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 14px', borderRadius: 6,
              border: `1px solid ${APP.border}`, cursor: 'pointer',
              backgroundColor: 'transparent', color: APP.textPrimary,
              fontSize: 12, fontWeight: 600,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSave}
            onClick={() => canSave && onSave(trimmed)}
            style={{
              padding: '8px 14px', borderRadius: 6,
              border: 'none', cursor: canSave ? 'pointer' : 'not-allowed',
              backgroundColor: canSave ? PDF_VIEWER.fieldActive : APP.border,
              color: '#fff',
              fontSize: 12, fontWeight: 700,
              opacity: canSave ? 1 : 0.7,
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
